export type PropertyDocumentKind =
  | 'Stammakte'
  | 'Vertrag'
  | 'Rechnung'
  | 'AusgangsRechnung'
  | 'Abrechnung'
  | 'SteuerLaufend'
  | 'BehoerdeStamm'
  | 'WartungReparatur'
  | 'InventarListe'
  | 'Garantie'
  | 'Bedienungsanleitung'
  | 'FotoMarketing'
  | 'FotoZustand'
  | 'FotoInventar'
  | 'FotoSchaden'
  | 'FotoReinigung'
  | 'SchluesselZugang'
  | 'Sonstiges';

export interface PropertyFileResponse {
  id: string;
  propertyId: string;
  kind: PropertyDocumentKind;
  folder: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  counterparty: string | null;
  amount: number | null;
  currency: string | null;
  issuedOn: string | null;
  validFrom: string | null;
  validTo: string | null;
  description: string | null;
  relatedFileId: string | null;
  uploadedAt: string;
  uploadedByUserId: string;
  /** GoBD-/System-Beleg: gesperrt gegen Move/Reclassify/Delete/Edit. */
  locked: boolean;
  /** Aufbewahrungsfrist (ISO-Datum) — nur bei gesperrten Belegen gesetzt. */
  retentionUntil: string | null;
}

export interface UploadPropertyFileRequest {
  kind: PropertyDocumentKind;
  counterparty?: string | null;
  amount?: number | null;
  currency?: string | null;
  issuedOn?: string | null;
  validFrom?: string | null;
  validTo?: string | null;
  description?: string | null;
  relatedFileId?: string | null;
  /** Ziel-Ordner (nur Custom-Ordner der Kind-Kategorie) — sonst kanonische Ablage. */
  targetFolder?: string | null;
}

export interface UpdatePropertyFileMetadataRequest {
  counterparty?: string | null;
  amount?: number | null;
  currency?: string | null;
  issuedOn?: string | null;
  validFrom?: string | null;
  validTo?: string | null;
  description?: string | null;
  relatedFileId?: string | null;
}

export interface PropertyFileTreeNode {
  name: string;
  path: string;
  folders: PropertyFileTreeNode[];
  files: PropertyFileResponse[];
  /** Anzahl Dateien in diesem Node + allen Sub-Nodes rekursiv. Backend-berechnet. */
  descendantFileCount: number;
  /** Summe SizeBytes rekursiv. Backend-berechnet. */
  totalSizeBytes: number;
  /** True wenn Folder zum festen Geruest gehoert UND rekursiv leer ist. */
  isScaffold: boolean;
}

export interface MovePropertyFileRequest {
  targetFolder: string;
}

export interface ReclassifyPropertyFileRequest {
  kind: PropertyDocumentKind;
  issuedOn?: string | null;
}

export interface CreateFolderRequest {
  path: string;
}

export interface RenameFolderRequest {
  path: string;
  newName: string;
}

export interface PropertyFolderResponse {
  path: string;
}

/** Papierkorb-Eintrag: Herkunft (OriginalFolder/-FileName) treibt das Restore-UI. */
export interface TrashedFileResponse {
  id: string;
  kind: PropertyDocumentKind;
  originalFolder: string | null;
  originalFileName: string | null;
  contentType: string;
  sizeBytes: number;
  deletedAt: string | null;
  retentionUntil: string | null;
  locked: boolean;
}

export interface PropertyFileListFilter {
  kind?: PropertyDocumentKind;
  year?: number;
  activeOnly?: boolean;
  expiringWithinDays?: number;
  counterparty?: string;
}

export const PROPERTY_DOCUMENT_KIND_LABELS: Record<PropertyDocumentKind, string> = {
  Stammakte: 'Stammakte (Kauf, Grundbuch, Energie)',
  Vertrag: 'Vertrag (Strom, Internet, Versicherung)',
  Rechnung: 'Rechnung (monatlich)',
  AusgangsRechnung: 'Ausgangsrechnung (an Gäste)',
  Abrechnung: 'Abrechnung (jaehrlich)',
  SteuerLaufend: 'Steuer / Behoerde (laufend)',
  BehoerdeStamm: 'Behoerde (Stamm-Genehmigungen)',
  WartungReparatur: 'Wartung / Reparatur',
  InventarListe: 'Inventar-Liste',
  Garantie: 'Garantie / Quittung',
  Bedienungsanleitung: 'Bedienungsanleitung',
  FotoMarketing: 'Foto — Marketing',
  FotoZustand: 'Foto — Zustand / Check-in',
  FotoInventar: 'Foto — Inventar',
  FotoSchaden: 'Foto — Schaden',
  FotoReinigung: 'Foto — Reinigung',
  SchluesselZugang: 'Schluessel / Zugang',
  Sonstiges: 'Sonstiges',
};
